
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Brain, TrendingUp, CloudRain, Target, RefreshCw } from 'lucide-react';
import { EnhancedTrainingLoadDashboard } from './training-intelligence/EnhancedTrainingLoadDashboard';
import { PerformanceForecastingDashboard } from './training-intelligence/PerformanceForecastingDashboard';
import { EnvironmentalIntelligence } from './training-intelligence/EnvironmentalIntelligence';
import { AdvancedStravaAnalytics } from './training-intelligence/AdvancedStravaAnalytics';
import { RealTimeAnalyticsMonitor } from './training-intelligence/RealTimeAnalyticsMonitor';

interface TrainingIntelligenceHubProps {
  className?: string;
}

export const TrainingIntelligenceHub: React.FC<TrainingIntelligenceHubProps> = ({ className }) => {
  const [activeTab, setActiveTab] = useState('training-load');

  const tabs = [
    {
      id: 'training-load',
      label: 'Training Load',
      icon: TrendingUp,
      description: 'Advanced load monitoring & injury risk assessment'
    },
    {
      id: 'forecasting',
      label: 'Performance Forecast',
      icon: Target,
      description: 'Race predictions & training trajectory analysis'
    },
    {
      id: 'environmental',
      label: 'Environmental Intel',
      icon: CloudRain,
      description: 'Weather impact & optimal condition analysis'
    },
    {
      id: 'strava-advanced',
      label: 'Strava Analytics',
      icon: Brain,
      description: 'Deep Strava metrics & segment performance'
    },
    {
      id: 'real-time',
      label: 'Live Monitoring',
      icon: RefreshCw,
      description: 'Real-time analytics & performance alerts'
    }
  ];

  return (
    <div className={className}>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-full bg-gradient-to-r from-purple-600 to-blue-600">
            <Brain className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Training Intelligence Hub
            </h1>
            <p className="text-muted-foreground">
              Advanced analytics powered by your Strava data and machine learning insights
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 mt-3">
          <Badge variant="secondary" className="bg-green-100 text-green-800">
            56 Strava Activities
          </Badge>
          <Badge variant="secondary" className="bg-blue-100 text-blue-800">
            Real-time Analytics
          </Badge>
          <Badge variant="secondary" className="bg-purple-100 text-purple-800">
            AI-Powered Insights
          </Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full h-auto grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1 p-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="flex flex-col sm:flex-row items-center sm:justify-center gap-1 sm:gap-2 p-2 h-auto min-h-[60px] text-center text-xs sm:text-sm whitespace-normal data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <div className="text-center space-y-0.5 overflow-hidden">
                  <div className="font-medium text-xs sm:text-sm leading-tight">{tab.label}</div>
                  <div className="text-[10px] text-muted-foreground leading-tight hidden xl:block max-w-[90px] mx-auto">
                    {tab.description}
                  </div>
                </div>
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="training-load" className="space-y-6">
          <EnhancedTrainingLoadDashboard />
        </TabsContent>

        <TabsContent value="forecasting" className="space-y-6">
          <PerformanceForecastingDashboard />
        </TabsContent>

        <TabsContent value="environmental" className="space-y-6">
          <EnvironmentalIntelligence />
        </TabsContent>

        <TabsContent value="strava-advanced" className="space-y-6">
          <AdvancedStravaAnalytics />
        </TabsContent>

        <TabsContent value="real-time" className="space-y-6">
          <RealTimeAnalyticsMonitor />
        </TabsContent>
      </Tabs>
    </div>
  );
};
